import React, { useMemo } from 'react';
import { JsonComponentSwitchProps } from './JsonComponentSwitch';
import { JsonDataContainerFragment } from '@graphql/generated';
import { CustomComponentsWrapperType } from '@website-editor/customComponents/CustomComponents';
import { CustomComponentWrapper } from './ComponentWrapper';
import { CUSTOM_JSON_COMPONENT_TYPES } from '@apps/websiteCustomJsonComponents/config';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useThingsToDoExperimentV2 } from './ThingsToDo/useThingsToDoExperimentV2';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import type { CustomJSONComponentTypes } from '@apps/websiteCustomJsonComponents/JsonComponent.types';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';

/**
 * Checks if a custom JSON component exists in the registry.
 *
 * Supports both component type and schema URL matching for flexibility.
 * Also includes legacy type compatibility for backwards compatibility.
 *
 * @param type - The component type identifier (e.g., 'GeneralSchedule', 'Vip')
 * @param schema - Optional schema URL. When provided, matches by either schema URL or component type.
 *                 This is useful when the component data might use schema-based identification
 *                 (for versioned schemas) or type-based identification.
 * @returns true if the component exists in the registry or is a legacy type, false otherwise
 */
export const doesComponentExist = (type: string, schema?: string): boolean => {
  const isLegacy = Object.values(LegacyCompatTypes).includes(type as LegacyCompatTypes);
  if (schema) {
    return isLegacy || CUSTOM_JSON_COMPONENT_TYPES.some(c => c.schemaUrl === schema || c.componentType === type);
  }
  return isLegacy || CUSTOM_JSON_COMPONENT_TYPES.some(c => c.componentType === type);
};

// Due to some poor initial planning:
// `GeneralEventSchedule` is maintained for backwards compat
// `https://withjoy.com/assets/public/schemas/VIPCustomComponent.json` is maintained for backwards compat
// DO NOT add to this enum.
// Once we fully stop setting these types, we should migrate the old types in the db to the new ones, and delete this.
enum LegacyCompatTypes {
  'legacySchedule' = 'GeneralEventSchedule', // We had a temporary mismatch in types. Unified to `GeneralSchedule`
  'legacyVIP' = 'https://withjoy.com/assets/public/schemas/VIPCustomComponent.json', // We were using the schemaURL as the type. Since it is versioned, this is bad. Replaced with `VIP`
}

// Components that should only be available for general events.
// Note: This array is kept for documentation. The switch statement in useGetJSONComponents
// should be kept in sync with these values.
export const GENERAL_ONLY_COMPONENTS: readonly (CustomJSONComponentTypes | LegacyCompatTypes)[] = [
  'GeneralSchedule',
  LegacyCompatTypes.legacySchedule, // legacy
  'FormDisplay',
  LegacyCompatTypes.legacyVIP, // legacy
];

// Component section categorization for AddComponentList mode
export const PLAN_YOUR_TRIP_COMPONENTS: readonly CustomJSONComponentTypes[] = [
  'FormDisplay',
  'StayBookingAssistant',
  'PlaceToStayComponent',
  'ThingsToDo',
  'DefaultThingsToDo',
  'WeatherReport',
];

export const EVENT_DETAILS_COMPONENTS: readonly CustomJSONComponentTypes[] = ['Faq', 'Vip', 'EventCountdown', 'RsvpBlock', 'GraphicAccent', 'EventDetails', 'GeneralSchedule'];

export type ComponentSection = 'planYourTrip' | 'eventDetails' | 'other';

export interface GroupedJsonComponents
  extends Readonly<{
    planYourTripComponents: ReadonlyArray<JSX.Element>;
    eventDetailsComponents: ReadonlyArray<JSX.Element>;
    otherComponents: ReadonlyArray<JSX.Element>;
  }> {}

export const useGetJSONComponents = (props: JsonComponentSwitchProps, data: JsonDataContainerFragment) => {
  const { wrapperType, pageSlug } = props;
  const [isPlaceToStayEnabled] = useIsFeatureEnabled('placeToStayInCustomPage');
  const [isStayBookingAssistantEnabled] = useIsFeatureEnabled('stayBookingAssistant');
  const [isThingsToDoEnabled] = useIsFeatureEnabled('websiteThingsToDoEnabled');
  const [isWeatherReportEnabled] = useIsFeatureEnabled('websiteWeatherReportEnabled');
  const { isMigratedCustomPageEnabled, isLoading: isWebsiteCustomEventPagesEnabledLoading } = useMigratedCustomPageFeatureFlags();
  const { componentLayout } = useThingsToDoExperimentV2();
  const useProWebComponents = useProFeature(EventFeature.websiteComponents);
  const isCashFundComponentEnabled = useProFeature(EventFeature.websiteCashFundComponent);

  // Filter component types based on feature flags
  const filteredComponents = CUSTOM_JSON_COMPONENT_TYPES.filter(component => {
    // If component is a ThingsToDo component, only show when feature flag is enabled
    // Type casting due to legacy compat types
    // Keep cases sorted alphabetically within each group
    switch (component.componentType as CustomJSONComponentTypes | LegacyCompatTypes) {
      case 'CashFundComponent':
        return isCashFundComponentEnabled;
      case 'DefaultThingsToDo':
        return isThingsToDoEnabled && !componentLayout;
      case 'ThingsToDo':
        return isThingsToDoEnabled;
      case 'FormDisplay':
      case 'GeneralSchedule':
      case LegacyCompatTypes.legacySchedule:
      case LegacyCompatTypes.legacyVIP:
        return useProWebComponents;
      case 'PlaceToStayComponent':
        return isPlaceToStayEnabled;
      case 'StayBookingAssistant':
        return isStayBookingAssistantEnabled;
      case 'WeatherReport':
        return isWeatherReportEnabled;
      case 'EventCountdown':
      case 'EventDetails':
      case 'GraphicAccent':
      case 'RsvpBlock':
        return isMigratedCustomPageEnabled(pageSlug) && !isWebsiteCustomEventPagesEnabledLoading;
      default:
        return true;
    }
  });

  let componentsToShow: typeof filteredComponents;
  if (wrapperType === CustomComponentsWrapperType.AddComponentList) {
    componentsToShow = filteredComponents;
  } else {
    componentsToShow = filteredComponents.filter(c => c.schemaUrl === data?.schemaUrl);
  }

  const allComponents = componentsToShow.map((component, index) => {
    const key = `jsoncomponent-wrapper-subcomponent-${component.schemaUrl}-${index}`;

    if (!component) {
      return null;
    }

    return (
      <CustomComponentWrapper
        component={component}
        key={key}
        {...props}
        data={data}
      />
    );
  });

  return allComponents.filter(c => !!c);
};

/**
 * Returns JSON components grouped by section for AddComponentList mode.
 * For other modes, returns all components in 'otherComponents'.
 */
export const useGetGroupedJSONComponents = (props: JsonComponentSwitchProps, data: JsonDataContainerFragment): GroupedJsonComponents => {
  const { wrapperType, pageSlug } = props;
  const [isPlaceToStayEnabled] = useIsFeatureEnabled('placeToStayInCustomPage');
  const [isStayBookingAssistantEnabled] = useIsFeatureEnabled('stayBookingAssistant');
  const [isThingsToDoEnabled] = useIsFeatureEnabled('websiteThingsToDoEnabled');
  const [isWeatherReportEnabled] = useIsFeatureEnabled('websiteWeatherReportEnabled');
  const { isMigratedCustomPageEnabled, isLoading: isWebsiteCustomEventPagesEnabledLoading } = useMigratedCustomPageFeatureFlags();
  const { componentLayout } = useThingsToDoExperimentV2();
  const useProWebComponents = useProFeature(EventFeature.websiteComponents);
  const isCashFundComponentEnabled = useProFeature(EventFeature.websiteCashFundComponent);

  // Filter component types based on feature flags
  const filteredComponents = CUSTOM_JSON_COMPONENT_TYPES.filter(component => {
    switch (component.componentType as CustomJSONComponentTypes | LegacyCompatTypes) {
      case 'CashFundComponent':
        return isCashFundComponentEnabled;
      case 'DefaultThingsToDo':
        return isThingsToDoEnabled && !componentLayout;
      case 'ThingsToDo':
        return isThingsToDoEnabled;
      case 'FormDisplay':
      case 'GeneralSchedule':
      case LegacyCompatTypes.legacySchedule:
      case LegacyCompatTypes.legacyVIP:
        return useProWebComponents;
      case 'PlaceToStayComponent':
        return isPlaceToStayEnabled;
      case 'StayBookingAssistant':
        return isStayBookingAssistantEnabled;
      case 'WeatherReport':
        return isWeatherReportEnabled;
      case 'EventCountdown':
      case 'EventDetails':
      case 'GraphicAccent':
      case 'RsvpBlock':
        return isMigratedCustomPageEnabled(pageSlug) && !isWebsiteCustomEventPagesEnabledLoading;
      default:
        return true;
    }
  });

  let componentsToShow: typeof filteredComponents;
  if (wrapperType === CustomComponentsWrapperType.AddComponentList) {
    componentsToShow = filteredComponents;
  } else {
    componentsToShow = filteredComponents.filter(c => c.schemaUrl === data?.schemaUrl);
  }

  // Categorize components by section
  const grouped: GroupedJsonComponents = useMemo(() => {
    const planYourTripComponents: JSX.Element[] = [];
    const eventDetailsComponents: JSX.Element[] = [];
    const otherComponents: JSX.Element[] = [];

    componentsToShow.forEach((component, index) => {
      if (!component) {
        return;
      }

      const key = `jsoncomponent-wrapper-subcomponent-${component.schemaUrl}-${index}`;
      const componentElement = (
        <CustomComponentWrapper
          component={component}
          key={key}
          {...props}
          data={data}
        />
      );

      const componentType = component.componentType as CustomJSONComponentTypes;

      if (PLAN_YOUR_TRIP_COMPONENTS.includes(componentType)) {
        planYourTripComponents.push(componentElement);
      } else if (EVENT_DETAILS_COMPONENTS.includes(componentType)) {
        eventDetailsComponents.push(componentElement);
      } else {
        otherComponents.push(componentElement);
      }
    });

    return {
      planYourTripComponents,
      eventDetailsComponents,
      otherComponents,
    };
  }, [componentsToShow, props, data]);

  return grouped;
};
